import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import TrashIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Trash.js';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import * as styles from 'src/components/atlas/molecules/FilterPopover/FilterPopover.styles';
import { CustomFieldFilterColumn } from 'src/graphql/generated';
import {
  CustomFieldFilterLogic,
  CustomFieldFilterRow,
} from './customFields.types';
import { filterButton } from './CustomFieldsPopover.styles';

const COLUMN_OPTIONS: { value: CustomFieldFilterColumn; label: string }[] = [
  { value: CustomFieldFilterColumn.Label, label: 'Label' },
  { value: CustomFieldFilterColumn.Status, label: 'Status' },
  { value: CustomFieldFilterColumn.FieldType, label: 'Type' },
  { value: CustomFieldFilterColumn.SectionId, label: 'Section' },
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const TYPE_OPTIONS = [
  { value: 'TEXT', label: 'Text input' },
  { value: 'SINGLE_SELECT', label: 'Single select' },
];

const LOGIC_OPTIONS: { value: CustomFieldFilterLogic; label: string }[] = [
  { value: 'and', label: 'And' },
  { value: 'or', label: 'Or' },
];

export interface CustomFieldsFilterPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  rows: CustomFieldFilterRow[];
  onRowsChange: (rows: CustomFieldFilterRow[]) => void;
  onRemoveAll: () => void;
  onApply: () => void;
  makeId: () => string;
  sectionOptions: { value: string; label: string }[];
}

export const CustomFieldsFilterPopover: React.FC<
  CustomFieldsFilterPopoverProps
> = ({
  anchorEl,
  open,
  onClose,
  rows,
  onRowsChange,
  onRemoveAll,
  onApply,
  makeId,
  sectionOptions,
}) => {
  const chosenColumns = useMemo(
    () => rows.map((r) => r.column).filter(Boolean),
    [rows],
  );

  // Switching column discards the old value, which only made sense for the
  // previous column.
  const handleColumnChange = (
    rowId: string,
    nextColumn: CustomFieldFilterColumn,
  ) => {
    onRowsChange(
      rows.map((r) =>
        r.id === rowId && r.column !== nextColumn
          ? { ...r, column: nextColumn, value: '' }
          : r,
      ),
    );
  };

  const handleValueChange = (rowId: string, next: string) => {
    onRowsChange(rows.map((r) => (r.id === rowId ? { ...r, value: next } : r)));
  };

  const handleLogicChange = (rowId: string, logic: CustomFieldFilterLogic) => {
    onRowsChange(rows.map((r) => (r.id === rowId ? { ...r, logic } : r)));
  };

  const handleAddFilter = () => {
    onRowsChange([
      ...rows,
      { id: makeId(), column: '', value: '', logic: 'and' },
    ]);
  };

  const handleRemoveRow = (rowId: string) => {
    onRowsChange(rows.filter((r) => r.id !== rowId));
  };

  const dropdownOptionsFor = (
    column: CustomFieldFilterColumn,
  ): { value: string; label: string }[] => {
    if (column === CustomFieldFilterColumn.Status) return STATUS_OPTIONS;
    if (column === CustomFieldFilterColumn.FieldType) return TYPE_OPTIONS;
    if (column === CustomFieldFilterColumn.SectionId) return sectionOptions;
    return [];
  };

  const renderValueControl = (row: CustomFieldFilterRow) => {
    if (!row.column) {
      return (
        <TextField
          size="small"
          disabled
          label="Value"
          placeholder="Filter value"
          sx={styles.field}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'aria-label': 'Filter value' }}
        />
      );
    }

    if (row.column === CustomFieldFilterColumn.Label) {
      return (
        <TextField
          size="small"
          label="Value"
          placeholder="Filter value"
          value={row.value}
          onChange={(e) => handleValueChange(row.id, e.target.value)}
          sx={styles.field}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-testid': 'custom-fields-filter-value-label' }}
        />
      );
    }

    const column = row.column;
    const options = dropdownOptionsFor(column);
    const selectedValue = row.value;

    return (
      <FormControl size="small" sx={styles.field}>
        <InputLabel shrink>Value</InputLabel>
        <Select
          displayEmpty
          label="Value"
          value={selectedValue}
          onChange={(e) => handleValueChange(row.id, e.target.value)}
          inputProps={{ 'aria-label': `Filter value for ${column}` }}
          SelectDisplayProps={
            {
              'data-testid': `custom-fields-filter-value-${column}`,
            } as React.HTMLAttributes<HTMLDivElement>
          }
          renderValue={(selected) =>
            selected ? (
              (options.find((o) => o.value === selected)?.label ?? selected)
            ) : (
              <Typography component="span" sx={styles.placeholderText}>
                Select a value
              </Typography>
            )
          }
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: styles.popoverPaper } }}
    >
      <Box sx={styles.container} data-testid="custom-fields-filter-popover">
        <Stack spacing={1.5}>
          {rows.map((row, index) => (
            <Stack
              key={row.id}
              direction="row"
              spacing={1.5}
              sx={styles.row}
              data-testid="custom-fields-filter-row"
            >
              <IconButton
                aria-label="Remove filter"
                size="small"
                disabled={rows.length <= 1}
                onClick={() => handleRemoveRow(row.id)}
                data-testid="custom-fields-filter-remove-row"
                sx={styles.removeIcon}
              >
                <CloseIcon size="md" />
              </IconButton>

              {index === 0 ? (
                <Box sx={styles.logicControl} />
              ) : (
                <FormControl size="small" sx={styles.logicControl}>
                  <Select
                    value={row.logic ?? 'and'}
                    onChange={(e) =>
                      handleLogicChange(
                        row.id,
                        e.target.value as CustomFieldFilterLogic,
                      )
                    }
                    inputProps={{
                      'aria-label': 'Logic operator',
                      'data-testid': 'custom-fields-filter-logic',
                    }}
                  >
                    {LOGIC_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl size="small" sx={styles.field}>
                <InputLabel shrink>Columns</InputLabel>
                <Select
                  displayEmpty
                  label="Columns"
                  value={row.column}
                  onChange={(e) =>
                    handleColumnChange(
                      row.id,
                      e.target.value as CustomFieldFilterColumn,
                    )
                  }
                  inputProps={{ 'aria-label': 'Choose a column' }}
                  SelectDisplayProps={
                    {
                      'data-testid': 'custom-fields-filter-column',
                    } as React.HTMLAttributes<HTMLDivElement>
                  }
                  renderValue={(selected) =>
                    selected ? (
                      COLUMN_OPTIONS.find((c) => c.value === selected)?.label
                    ) : (
                      <Typography component="span" sx={styles.placeholderText}>
                        Choose a column
                      </Typography>
                    )
                  }
                >
                  {COLUMN_OPTIONS.map((col) => (
                    <MenuItem
                      key={col.value}
                      value={col.value}
                      disabled={
                        col.value !== row.column &&
                        chosenColumns.includes(col.value)
                      }
                    >
                      {col.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {renderValueControl(row)}
            </Stack>
          ))}
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={styles.footer}
        >
          <Button
            variant="text"
            onClick={handleAddFilter}
            data-testid="custom-fields-filter-add"
            startIcon={<AddIcon />}
            style={filterButton}
          >
            Add filter
          </Button>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="text"
              onClick={onRemoveAll}
              data-testid="custom-fields-filter-remove-all"
              startIcon={<TrashIcon />}
              style={filterButton}
            >
              Remove all
            </Button>
            <Button
              variant="contained"
              onClick={onApply}
              data-testid="custom-fields-filter-apply"
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Popover>
  );
};
